import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { toast } from "sonner";
import { Search, Trash2, Plus, Loader2, MapPin } from "lucide-react";

L.Marker.prototype.options.icon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const DEFAULT_CENTER = [39.3621, 22.942]; // Βόλος

const Recenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, 16);
  }, [position && position[0], position && position[1]]); // eslint-disable-line
  return null;
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
};

const LocationRow = ({ loc, index, canRemove, onChange, onRemove }) => {
  const [searching, setSearching] = useState(false);
  const hasPos = loc.lat != null && loc.lng != null;
  const position = hasPos ? [loc.lat, loc.lng] : null;

  const geocode = async () => {
    if (!loc.address?.trim()) {
      toast.error("Γράψτε πρώτα μια διεύθυνση");
      return;
    }
    setSearching(true);
    try {
      const q = encodeURIComponent(loc.address);
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`);
      const data = await res.json();
      if (data && data[0]) {
        onChange({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
        toast.success("Βρέθηκε η τοποθεσία — σύρε τον δείκτη για ακρίβεια");
      } else {
        toast.error("Δεν βρέθηκε η διεύθυνση. Κάνε κλικ στον χάρτη.");
      }
    } catch {
      toast.error("Αποτυχία αναζήτησης");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border p-3 space-y-2" data-testid={`location-row-${index}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-muted-foreground flex items-center gap-1"><MapPin size={13} /> Διεύθυνση {index + 1}</span>
        {canRemove && (
          <button type="button" onClick={onRemove} data-testid={`location-remove-${index}`} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-muted text-destructive">
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <div className="flex gap-2">
        <input
          value={loc.address || ""}
          onChange={(e) => onChange({ address: e.target.value })}
          placeholder="π.χ. Δημητριάδος 100, Βόλος"
          data-testid={`location-address-${index}`}
          className="flex-1 h-10 px-3 rounded-xl bg-muted text-sm outline-none focus:ring-2 ring-primary"
        />
        <button
          type="button"
          onClick={geocode}
          disabled={searching}
          data-testid={`location-search-${index}`}
          className="h-10 px-3 rounded-xl bg-primary text-white text-sm font-semibold flex items-center gap-1.5 shrink-0"
        >
          {searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Αναζήτηση
        </button>
      </div>
      <div className="rounded-xl overflow-hidden border border-border">
        <MapContainer center={position || DEFAULT_CENTER} zoom={position ? 16 : 12} style={{ height: "14rem", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickHandler onPick={(lat, lng) => onChange({ lat, lng })} />
          {position && (
            <Marker
              draggable
              position={position}
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  onChange({ lat, lng });
                },
              }}
            />
          )}
          <Recenter position={position} />
        </MapContainer>
      </div>
      <p className="text-xs text-muted-foreground">
        {hasPos ? `Συντεταγμένες: ${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)} — σύρε τον δείκτη ή κάνε κλικ στον χάρτη` : "Κάνε κλικ στον χάρτη ή πάτα Αναζήτηση για να ορίσεις το σημείο"}
      </p>
    </div>
  );
};

export const LocationPicker = ({ value = [], onChange }) => {
  const locations = value.length ? value : [{ address: "", lat: null, lng: null }];

  const update = (i, patch) => onChange(locations.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const add = () => onChange([...locations, { address: "", lat: null, lng: null }]);
  const remove = (i) => {
    const next = locations.filter((_, idx) => idx !== i);
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="location-picker">
      <label className="block text-sm font-semibold">Τοποθεσίες</label>
      {locations.map((loc, i) => (
        <LocationRow
          key={i}
          loc={loc}
          index={i}
          canRemove={locations.length > 1}
          onChange={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
        />
      ))}
      <button type="button" onClick={add} data-testid="location-add-button" className="text-sm font-semibold text-primary flex items-center gap-1">
        <Plus size={15} /> Προσθήκη διεύθυνσης
      </button>
    </div>
  );
};
